import { fetchWithAuth } from './fetch-with-auth';
import { API_URL } from 'constants/index';

/**
 * De endpoints achter de knop **AI-voorstel** in het antwoordvenster.
 *
 * Helpdesk en renovatietickets hebben elk hun eigen route maar leveren dezelfde
 * soort tekst, dus staan ze hier samen: wie de knop ergens anders wil, ziet in één
 * bestand wat er nodig is.
 *
 * **Er vertrekt nooit iets vanzelf.** Deze aanroepen schrijven een tekst; het
 * verzenden blijft een aparte, menselijke handeling.
 */

/**
 * Maximum voor de instructie van de medewerker; de backend kapt op hetzelfde getal
 * af. Ruim genoeg voor "meld dat de technieker dinsdag tussen 9 en 12u komt", te
 * weinig om een volledige mail in te plakken — daarvoor is het tekstvak zelf.
 */
export const MAX_AI_INSTRUCTION_LENGTH = 1000;

/** Een voorgesteld antwoord zoals het antwoordvenster het gebruikt. */
export interface AiReplySuggestion {
  /** Alleen bij de helpdesk: daar wordt het voorstel bewaard en afgesloten. */
  id?: number;
  subject?: string;
  body: string;
  language?: string | null;
  /** Wat de medewerker er bij het schrijven zelf bij vroeg, of `null`. */
  instruction?: string | null;
  /** De mail waarop het voorstel antwoordt, of `null` bij een eerste bericht. */
  inReplyToMessageId?: string | null;
  createdAt?: string;
}

/** Naar wie het voorstel geschreven wordt. */
export type AiReplyAudience = 'tenant' | 'supplier';

type Session = Parameters<typeof fetchWithAuth>[2];

/** Haalt de foutuitleg van de backend, zodat de gebruiker weet waarom er niets staat. */
async function readError(response: Response, terugval: string): Promise<Error> {
  try {
    const body = await response.json();
    return new Error(body?.message || terugval);
  } catch {
    return new Error(terugval);
  }
}

/**
 * Het klaarstaande voorstel van een helpdeskticket, of `null`.
 *
 * Bij een inkomende huurdersmail schrijft de backend automatisch een voorstel; dit
 * haalt dat op. `null` is een normale uitkomst — niet elk ticket heeft er een — dus
 * de UI toont dan enkel de knop.
 */
export async function fetchHelpdeskReplySuggestion(
  ticketId: number | string,
  audience: AiReplyAudience,
  session: Session,
): Promise<AiReplySuggestion | null> {
  const response = await fetchWithAuth(
    `${API_URL}/helpdesk/ticket/${ticketId}/reply-suggestion?audience=${audience}`,
    { method: 'GET' },
    session,
  );
  if (!response?.ok) return null;
  return (await response.json()) ?? null;
}

/**
 * Laat de AI nu een antwoord schrijven voor een helpdeskticket.
 *
 * @param instruction Optioneel: wat de medewerker er zelf bij wil ("meld dat de
 *   technieker dinsdag komt"). Leeg laten = enkel de ticketgegevens en het verloop.
 */
export async function generateHelpdeskReplySuggestion(
  ticketId: number | string,
  audience: AiReplyAudience,
  session: Session,
  instruction?: string,
): Promise<AiReplySuggestion> {
  const response = await fetchWithAuth(
    `${API_URL}/helpdesk/ticket/${ticketId}/reply-suggestion/generate`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ audience, instruction: instruction?.trim() || undefined }),
    },
    session,
  );
  if (!response?.ok) {
    throw await readError(response, 'Het AI-voorstel kon niet geschreven worden');
  }
  return response.json();
}

/**
 * Meldt dat een bewaard voorstel afgehandeld is, zodat het niet opnieuw opduikt.
 *
 * Enkel voor de helpdesk: daar wordt het voorstel bewaard. Bij renovatietickets
 * bestaat het alleen zolang het venster open staat.
 */
export async function closeHelpdeskReplySuggestion(
  suggestionId: number,
  outcome: 'used' | 'dismissed',
  session: Session,
): Promise<void> {
  await fetchWithAuth(
    `${API_URL}/helpdesk/reply-suggestion/${suggestionId}/close`,
    {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ outcome }),
    },
    session,
  );
}

/**
 * Laat de AI nu een antwoord schrijven voor een renovatieticket.
 *
 * `channel` bepaalt naar wie: `supplier` (RS) of `tenant` (RT). De backend neemt
 * het verloop van dát kanaal als context.
 *
 * @param instruction Optioneel: wat de medewerker er zelf bij wil.
 */
export async function generateRenoReplySuggestion(
  ticketId: number | string,
  channel: 'supplier' | 'tenant',
  session: Session,
  instruction?: string,
): Promise<AiReplySuggestion> {
  const response = await fetchWithAuth(
    `${API_URL}/reno-helpdesk/tickets/${ticketId}/reply-suggestion`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ channel, instruction: instruction?.trim() || undefined }),
    },
    session,
  );
  if (!response?.ok) {
    throw await readError(response, 'Het AI-voorstel kon niet geschreven worden');
  }
  return response.json();
}
